import React, { useState, useEffect, useRef } from 'react';
import { baseUrl } from '../shared';
import './wallboard.css';

const STATUS_CARD_CLASS = {
  'Prebooking': 'wb-card-Prebooking',
  'Booked': 'wb-card-Booked',
  'Out': 'wb-card-Out',
  'Return Inspection': 'wb-card-ReturnInspection',
  'Returned': 'wb-card-Returned',
};


function startAutoScroll(el, animRef, durationMs) {
  if (!el) return;
  if (animRef.current) cancelAnimationFrame(animRef.current);
  el.scrollTop = 0;
  const maxScroll = el.scrollHeight - el.clientHeight;
  if (maxScroll <= 0) return;
  const startTime = performance.now();
  const animate = (now) => {
    const progress = Math.min((now - startTime) / durationMs, 1);
    el.scrollTop = maxScroll * progress;
    if (progress < 1) {
      animRef.current = requestAnimationFrame(animate);
    }
  };
  animRef.current = requestAnimationFrame(animate);
}

export default function Wallboard() {
  const [data, setData] = useState({ statusCounts: [], currentlyOut: [], upcoming: [], returned: [] });
  const [lastUpdated, setLastUpdated] = useState(null);
  const [intervalSecs, setIntervalSecs] = useState(30);
  const [error, setError] = useState('');
  const intervalRef = useRef(null);
  const upcomingScrollRef = useRef(null);
  const outScrollRef = useRef(null);
  const returnedScrollRef = useRef(null);
  const upcomingAnimRef = useRef(null);
  const outAnimRef = useRef(null);
  const returnedAnimRef = useRef(null);
  const [scale, setScale] = useState(1);
  const hasAppHeader = localStorage.getItem('adminFunctions') === '1';
  // Height of the redesigned app chrome shown to admins on the wallboard: a
  // single ~64px sticky AppBar (no fixed header/footer as before). Subtract it so
  // the 1920x1080 board scales to fit exactly with no scrollbar/overhang.
  const APP_HEADER_H = 64;

  useEffect(() => {
    const computeScale = () => {
      const availH = hasAppHeader ? window.innerHeight - APP_HEADER_H : window.innerHeight;
      setScale(Math.min(window.innerWidth / 1920, availH / 1080));
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, [hasAppHeader]);

  const fetchData = async () => {
    try {
      const res = await fetch(baseUrl + 'wallboard', {
        headers: {
          username: localStorage.getItem('username'),
          bToken: localStorage.getItem('bToken'),
        },
      });
      if (!res.ok) {
        if (res.status === 401) { window.location.href = '/'; return; }
        throw new Error('Server error');
      }
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date().toLocaleTimeString());
      setError('');
    } catch (e) {
      setError('Failed to refresh data');
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (intervalSecs >= 5) {
      intervalRef.current = setInterval(fetchData, intervalSecs * 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [intervalSecs]);

  useEffect(() => {
    const durationMs = intervalSecs * 1000;
    startAutoScroll(upcomingScrollRef.current, upcomingAnimRef, durationMs);
    startAutoScroll(outScrollRef.current, outAnimRef, durationMs);
    startAutoScroll(returnedScrollRef.current, returnedAnimRef, durationMs);
    return () => {
      if (upcomingAnimRef.current) cancelAnimationFrame(upcomingAnimRef.current);
      if (outAnimRef.current) cancelAnimationFrame(outAnimRef.current);
      if (returnedAnimRef.current) cancelAnimationFrame(returnedAnimRef.current);
    };
  }, [data, intervalSecs]);

  const overdue = data.currentlyOut.filter(r => r.daysOverdue > 0);
  const onTime = data.currentlyOut.filter(r => r.daysOverdue <= 0);

  return (
    <div className="wb-scale-outer" style={hasAppHeader ? { height: `calc(100vh - ${APP_HEADER_H}px)` } : {}}>
    <div style={{ width: 1920 * scale, height: 1080 * scale, overflow: 'hidden', flexShrink: 0 }}>
    <div className="wb-root" style={{ transform: `scale(${scale})` }}>

      {/* Header */}
      <div className="wb-header">
        <h1>1 Maintenance &amp; Supply — Hire Wallboard</h1>
        <div className="wb-header-right">
          {error && <span style={{ color: '#f85149' }}>{error}</span>}
          {lastUpdated && <span className="wb-last-updated">Last updated: {lastUpdated}</span>}
          <div className="wb-interval">
            <span>Refresh every</span>
            <input
              type="number"
              min="5"
              max="600"
              value={intervalSecs}
              onChange={e => setIntervalSecs(Number(e.target.value))}
            />
            <span>s</span>
          </div>
          <button
            className="wb-logout-btn"
            onClick={() => { localStorage.clear(); window.location.href = '/'; }}
          >
            Log out
          </button>
        </div>
      </div>

      {/* Status pipeline — Invoiced excluded (grows indefinitely) */}
      <div className="wb-pipeline">
        {data.statusCounts.filter(s => s.status !== 'Invoiced').map(s => (
          <div key={s.status} className={`wb-status-card ${STATUS_CARD_CLASS[s.status] || ''}`}>
            <div className="wb-status-count">{s.count}</div>
            <div className="wb-status-name">{s.status}</div>
          </div>
        ))}
      </div>

      {/* Main row: Upcoming (left) | Currently Out (right) */}
      <div className="wb-main">

        {/* Upcoming */}
        <div className="wb-panel">
          <div className="wb-panel-title">
            Upcoming — Next 30 Days <span>{data.upcoming.length}</span>
          </div>
          <div className="wb-table-wrap" ref={upcomingScrollRef}>
            <table className="wb-table">
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Description</th>
                  <th>Company</th>
                  <th>Start Date</th>
                  <th>Return Date</th>
                </tr>
              </thead>
              <tbody>
                {data.upcoming.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8b949e', padding: '16px' }}>No upcoming bookings in the next 30 days</td></tr>
                )}
                {data.upcoming.map(r => (
                  <tr key={r.id}>
                    <td>{r.itemNo}</td>
                    <td>{r.itemName}</td>
                    <td>{r.company}</td>
                    <td>{r.startDate}</td>
                    <td>{r.returnDate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right column — Currently Out (60%) over Returned (40%) */}
        <div className="wb-col-stack">

          {/* Currently Out */}
          <div className="wb-panel wb-panel-out">
            <div className="wb-panel-title">
              Currently Out <span>{data.currentlyOut.length}</span>
              {overdue.length > 0 && (
                <span style={{ color: '#f85149', marginLeft: 12, fontSize: 13 }}>
                  ({overdue.length} overdue)
                </span>
              )}
            </div>
            <div className="wb-table-wrap" ref={outScrollRef}>
              <table className="wb-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Company</th>
                    <th>Out</th>
                    <th>Due Back</th>
                  </tr>
                </thead>
                <tbody>
                  {data.currentlyOut.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8b949e', padding: '20px' }}>No items currently out</td></tr>
                  )}
                  {overdue.map(r => (
                    <tr key={r.id} className="wb-row-overdue">
                      <td>{r.itemNo}</td>
                      <td>{r.itemName}</td>
                      <td>{r.company}</td>
                      <td>{r.startDate}</td>
                      <td>{r.returnDate} (+{r.daysOverdue}d)</td>
                    </tr>
                  ))}
                  {onTime.map(r => (
                    <tr key={r.id}>
                      <td>{r.itemNo}</td>
                      <td>{r.itemName}</td>
                      <td>{r.company}</td>
                      <td>{r.startDate}</td>
                      <td>{r.returnDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Returned */}
          <div className="wb-panel wb-panel-returned">
            <div className="wb-panel-title">
              Returned <span>{data.returned.length}</span>
            </div>
            <div className="wb-table-wrap" ref={returnedScrollRef}>
              <table className="wb-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Description</th>
                    <th>Company</th>
                    <th>Out</th>
                    <th>Return Date</th>
                  </tr>
                </thead>
                <tbody>
                  {data.returned.length === 0 && (
                    <tr><td colSpan={5} style={{ textAlign: 'center', color: '#8b949e', padding: '20px' }}>No items in returned status</td></tr>
                  )}
                  {data.returned.map(r => (
                    <tr key={r.id}>
                      <td>{r.itemNo}</td>
                      <td>{r.itemName}</td>
                      <td>{r.company}</td>
                      <td>{r.startDate}</td>
                      <td>{r.returnDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>

      </div>

    </div>
    </div>
    </div>
  );
}
